import { TranslateService } from '@ngx-translate/core';
import { IBookingModel } from '@services/api/booking/booking-model.interface';
import { MessageService } from 'primeng/api';
import { inject, Injectable } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';

/**
 * Service to handle form validation logic for parking request forms
 */
@Injectable({
  providedIn: 'root'
})
export class ParkingRequestFormValidationService {
  private readonly translate = inject(TranslateService);
  /**
   * Set up form listeners for conditional validation
   */
  setupFormListeners(form: FormGroup): void {
    if (!form) return;

    // License plate fields are never required - removed validation logic

    // Listen for booking changes to update date constraints
    form.get('booking')?.valueChanges.subscribe((booking) => {
      if (booking && booking.start_date && booking.end_date) {
        const minDate = new Date(booking.start_date);
        const maxDate = new Date(booking.end_date);

        // Validate current dates against new constraints
        const startDate = form.get('start_date')?.value;
        const endDate = form.get('end_date')?.value;

        if (startDate && (startDate < minDate || startDate > maxDate)) {
          form.get('start_date')?.setValue(null);
        }

        if (endDate && (endDate < minDate || endDate > maxDate)) {
          form.get('end_date')?.setValue(null);
        }
      }
    });
  }

  /**
   * Validate that dates are within booking date range and logical
   */
  validateDates(form: FormGroup, messageService: MessageService): boolean {
    const booking = form.get('booking')?.value as IBookingModel;
    const startDate = form.get('start_date')?.value as Date;
    const endDate = form.get('end_date')?.value as Date;

    if (!booking) {
      messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.error'),
        detail: this.translate.instant('parking.form.noBookingSelected')
      });
      return false;
    }

    const bookingStartDate = new Date(booking.start_date!);
    const bookingEndDate = new Date(booking.end_date!);

    if (!startDate || !endDate) {
      messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.error'),
        detail: this.translate.instant('parking.form.selectBothDates')
      });
      return false;
    }

    if (startDate < bookingStartDate || startDate > bookingEndDate) {
      messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.error'),
        detail: this.translate.instant('parking.form.startDateOutOfRange')
      });
      return false;
    }

    if (endDate < bookingStartDate || endDate > bookingEndDate) {
      messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.error'),
        detail: this.translate.instant('parking.form.endDateOutOfRange')
      });
      return false;
    }

    if (endDate < startDate) {
      messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.error'),
        detail: this.translate.instant('parking.request.validation.end_date_before_start')
      });
      return false;
    }

    return true;
  }

  /**
   * Validate form before submission
   */
  validateFormForSubmission(form: FormGroup, messageService: MessageService): boolean {
    if (!form || form.invalid) {
      messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.error'),
        detail: this.translate.instant('parking.form.validationError')
      });
      return false;
    }

    return this.validateDates(form, messageService);
  }

  /**
   * Get date constraints from booking
   */
  getDateConstraints(booking: IBookingModel | null): { minDate: Date; maxDate: Date } | null {
    if (!booking || !booking.start_date || !booking.end_date) {
      return null;
    }

    return {
      minDate: new Date(booking.start_date),
      maxDate: new Date(booking.end_date)
    };
  }
}
